import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import PageHeader from "@/components/PageHeader";
import { EUR, fmtDate, donorName, initials } from "@/lib/format";
import { StatusBadge, DonorTypeBadge } from "@/components/StatusBadge";
import { PLEDGE, PAYMENT } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DonorFormDialog from "@/components/DonorFormDialog";
import { toast } from "sonner";
import { Plus, Mail, MessageSquare, StickyNote, Upload, ArrowLeft, Trash2, FileText, FileDown, File as FileIcon, Phone, Pencil } from "lucide-react";

export default function DonorDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: donor, isLoading } = useQuery({
    queryKey: ["donor", id],
    queryFn: async () => (await api.get(`/donors/${id}`)).data,
  });
  const { data: pledges = [] } = useQuery({
    queryKey: ["pledges", id],
    queryFn: async () => (await api.get("/pledges", { params: { donor_id: id } })).data,
  });
  const { data: schedules = [] } = useQuery({
    queryKey: ["schedules", id],
    queryFn: async () => (await api.get("/schedules", { params: { donor_id: id } })).data,
  });
  const { data: payments = [] } = useQuery({
    queryKey: ["payments", id],
    queryFn: async () => (await api.get("/payments", { params: { donor_id: id } })).data,
  });
  const { data: timeline = [] } = useQuery({
    queryKey: ["timeline", id],
    queryFn: async () => (await api.get(`/donors/${id}/timeline`)).data,
  });

  if (isLoading || !donor) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["donor", id] });
    qc.invalidateQueries({ queryKey: ["pledges", id] });
    qc.invalidateQueries({ queryKey: ["schedules", id] });
    qc.invalidateQueries({ queryKey: ["payments", id] });
    qc.invalidateQueries({ queryKey: ["timeline", id] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["donors"] });
  };

  const editMut = {
    mutate: async (payload) => {
      try {
        await api.patch(`/donors/${id}`, payload);
        toast.success("Donatore aggiornato");
        setEditOpen(false);
        refresh();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore");
      }
    },
    isPending: false,
  };

  const doDelete = async () => {
    try {
      await api.delete(`/donors/${id}`);
      toast.success("Donatore eliminato");
      qc.invalidateQueries({ queryKey: ["donors"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      navigate("/donatori");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
      setDeleteOpen(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to="/donatori" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13} /> Torna ai donatori
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
        <div className="flex gap-5 items-start">
          <div className="w-16 h-16 rounded-2xl bg-[#1C352D] text-white grid place-items-center text-xl font-semibold">
            {initials(donor)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-semibold tracking-tight text-[#1C352D]">{donorName(donor)}</h1>
              <DonorTypeBadge type={donor.type} />
              <StatusBadge status={donor.status} />
            </div>
            <div className="text-sm text-stone-500 mt-1">
              {[donor.city, donor.cf && `CF: ${donor.cf}`, donor.piva && `P.IVA: ${donor.piva}`].filter(Boolean).join(" · ") || "—"}
            </div>
            <div className="flex gap-4 text-xs text-stone-600 mt-2">
              {donor.email && <span className="inline-flex items-center gap-1"><Mail size={12}/>{donor.email}</span>}
              {donor.phone && <span className="inline-flex items-center gap-1"><MessageSquare size={12}/>{donor.phone}</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-stone-200 rounded-lg p-4 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Totale donato</div>
            <div className="text-2xl font-semibold mt-1">{EUR.format(donor.total_donated || 0)}</div>
            <div className="text-[11px] text-stone-500 mt-1">{donor.donations_count} donazioni</div>
          </div>
          <div className="border border-stone-200 rounded-lg p-4 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Ultimo pagamento</div>
            <div className="text-lg font-semibold mt-1">{fmtDate(donor.last_payment_date)}</div>
          </div>
          <div className="col-span-2 flex gap-2">
            <Button
              data-testid="donor-detail-edit"
              variant="outline"
              className="rounded-full flex-1"
              onClick={() => setEditOpen(true)}
            >
              <Pencil size={14} className="mr-1.5"/> Modifica
            </Button>
            <Button
              data-testid="donor-detail-delete"
              variant="outline"
              className="rounded-full flex-1 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 hover:border-red-300"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 size={14} className="mr-1.5"/> Elimina
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Tabs defaultValue="impegni">
            <TabsList className="bg-white border border-stone-200">
              <TabsTrigger value="impegni" data-testid="tab-impegni">Impegni</TabsTrigger>
              <TabsTrigger value="saldo" data-testid="tab-saldo">Saldo</TabsTrigger>
              <TabsTrigger value="scadenze" data-testid="tab-scadenze">Scadenze</TabsTrigger>
              <TabsTrigger value="pagamenti" data-testid="tab-pagamenti">Pagamenti</TabsTrigger>
              <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti</TabsTrigger>
              <TabsTrigger value="note" data-testid="tab-note">Note</TabsTrigger>
            </TabsList>

            <TabsContent value="impegni" className="mt-4">
              <PledgeCard donorId={id} pledges={pledges} onChange={refresh} />
            </TabsContent>
            <TabsContent value="saldo" className="mt-4">
              <BalanceCard donorId={id} />
            </TabsContent>
            <TabsContent value="scadenze" className="mt-4">
              <SchedulesCard schedules={schedules} donorId={id} pledges={pledges} onChange={refresh} />
            </TabsContent>
            <TabsContent value="pagamenti" className="mt-4">
              <PaymentsCard payments={payments} donorId={id} pledges={pledges} onChange={refresh} />
            </TabsContent>
            <TabsContent value="documenti" className="mt-4">
              <DocumentsCard donorId={id} />
            </TabsContent>
            <TabsContent value="note" className="mt-4">
              <NotesCard donor={donor} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-5">
          <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Timeline attività</div>
            <TimelineList events={timeline} />
          </Card>

          <CommunicationBox donorId={id} donor={donor} onSent={refresh} />
        </div>
      </div>

      <DonorFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        initialDonor={donor}
        onSubmit={(payload) => editMut.mutate(payload)}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {donorName(donor)}?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;operazione è irreversibile: verranno rimossi anche impegni, scadenze, pagamenti,
              comunicazioni e follow-up collegati. I documenti caricati resteranno archiviati come cancellati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="donor-detail-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="donor-detail-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={doDelete}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BalanceCard({ donorId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["donor-balance", donorId],
    queryFn: async () => (await api.get(`/donors/${donorId}/balance`)).data,
  });

  if (isLoading) return <div className="p-6 text-sm text-stone-400">Calcolo saldo in corso…</div>;

  if (!data?.has_pledge) {
    return (
      <Card className="p-8 text-center border border-stone-200 rounded-xl shadow-none" data-testid="balance-empty">
        <div className="text-sm text-stone-600 font-medium">Nessun impegno ricorrente attivo</div>
        <div className="text-xs text-stone-500 mt-1">Il saldo progressivo viene calcolato solo per impegni ricorrenti (mensile, trimestrale, ecc.).</div>
      </Card>
    );
  }

  const { pledge, current_balance, current_status, total_expected, total_paid, breakdown } = data;
  const statusStyle = {
    ok:      { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "In pari" },
    credit:  { cls: "text-sky-700 bg-sky-50 border-sky-200",             label: "In credito" },
    arrears: { cls: "text-red-700 bg-red-50 border-red-200",             label: "In ritardo" },
  }[current_status];
  const rowStyle = {
    ok:      "bg-white",
    credit:  "bg-sky-50/50",
    arrears: "bg-red-50/50",
  };

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("it-IT", { month: "short", year: "numeric" });
  };
  const freqLabel = { mensile: "mensile", trimestrale: "trimestrale", semestrale: "semestrale", annuale: "annuale" }[pledge.frequency] || pledge.frequency;

  return (
    <div className="space-y-4" data-testid="balance-card">
      {/* Riepilogo saldo */}
      <Card className={`p-5 border-2 rounded-2xl shadow-none ${statusStyle.cls}`} data-testid="balance-summary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Saldo attuale</div>
            <div className="text-3xl font-semibold tracking-tight mt-1">
              {current_balance >= 0 ? "+" : ""}{EUR.format(current_balance)}
            </div>
            <div className="text-xs mt-1 opacity-80">
              {statusStyle.label} · impegno {EUR.format(pledge.amount)} {freqLabel}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-right text-[13px]">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Dovuto</div>
              <div className="font-medium mt-0.5">{EUR.format(total_expected)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Pagato</div>
              <div className="font-medium mt-0.5">{EUR.format(total_paid)}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Breakdown mensile */}
      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/60">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Storico mensile</div>
          <div className="text-xs text-stone-500 mt-0.5">Righe rosse = ritardo cumulato, azzurre = credito. Un pagamento in eccesso compensa naturalmente i mesi scoperti.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-stone-500 bg-stone-50/40">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Mese</th>
                <th className="text-right px-3 py-2 font-medium">Dovuto</th>
                <th className="text-right px-3 py-2 font-medium">Pagato</th>
                <th className="text-right px-3 py-2 font-medium">Δ mese</th>
                <th className="text-right px-4 py-2 font-medium">Saldo cumulato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {breakdown.map((row) => (
                <tr key={row.month} data-testid={`balance-row-${row.month}`} className={rowStyle[row.status]}>
                  <td className="px-4 py-2 capitalize">{monthLabel(row.month)}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-stone-600">
                    {row.expected > 0 ? EUR.format(row.expected) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums font-medium">
                    {row.paid > 0 ? EUR.format(row.paid) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className={`text-right px-3 py-2 tabular-nums ${row.delta > 0 ? "text-sky-700" : row.delta < 0 ? "text-red-700" : "text-stone-400"}`}>
                    {row.delta === 0 ? "—" : (row.delta > 0 ? "+" : "") + EUR.format(row.delta)}
                  </td>
                  <td className={`text-right px-4 py-2 tabular-nums font-semibold ${
                    row.status === "arrears" ? "text-red-700" :
                    row.status === "credit"  ? "text-sky-700"  : "text-emerald-700"
                  }`}>
                    {row.cumulative_balance >= 0 ? "+" : ""}{EUR.format(row.cumulative_balance)}
                  </td>
                </tr>
              ))}
              {breakdown.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-stone-400 text-xs">Nessun mese da mostrare.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PledgeCard({ donorId, pledges, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // pledge object being edited
  const [deletePledgeId, setDeletePledgeId] = useState(null);
  const emptyForm = {
    kind: "ricorrente", amount: 50, frequency: "mensile",
    method: "bonifico", start_date: new Date().toISOString().slice(0, 10),
    status: "attivo", notes: "",
  };
  const [form, setForm] = useState(emptyForm);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      kind: p.kind,
      amount: p.amount,
      frequency: p.frequency,
      method: p.method || "bonifico",
      start_date: p.start_date,
      status: p.status || "attivo",
      notes: p.notes || "",
    });
    setOpen(true);
  };
  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = { ...form, amount: parseFloat(form.amount) };
      if (editing) {
        return (await api.patch(`/pledges/${editing.id}`, body)).data;
      }
      return (await api.post("/pledges", { donor_id: donorId, ...body })).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Impegno aggiornato" : "Impegno creato · scadenze generate");
      setOpen(false);
      setEditing(null);
      onChange();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/pledges/${id}`)).data,
    onSuccess: () => { toast.success("Impegno eliminato"); setDeletePledgeId(null); onChange(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore eliminazione"),
  });

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-4 flex items-center justify-between border-b border-stone-100">
        <div className="text-sm font-medium">Impegni di donazione</div>
        <Button size="sm" data-testid={PLEDGE.addBtn} onClick={openNew} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
          <Plus size={14} className="mr-1"/> Nuovo impegno
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifica impegno" : "Nuovo impegno di donazione"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, frequency: v === "una_tantum" ? "una_tantum" : (form.frequency === "una_tantum" ? "mensile" : form.frequency) })}>
                  <SelectTrigger data-testid={PLEDGE.kind} className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ricorrente">Ricorrente</SelectItem>
                    <SelectItem value="una_tantum">Una tantum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequenza</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })} disabled={form.kind === "una_tantum"}>
                  <SelectTrigger data-testid={PLEDGE.frequency} className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensile">Mensile</SelectItem>
                    <SelectItem value="trimestrale">Trimestrale</SelectItem>
                    <SelectItem value="semestrale">Semestrale</SelectItem>
                    <SelectItem value="annuale">Annuale</SelectItem>
                    <SelectItem value="una_tantum">Una tantum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Importo (€)</Label>
                <Input data-testid={PLEDGE.amount} type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5"/>
              </div>
              <div>
                <Label>Metodo previsto</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid={PLEDGE.method} className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data primo pagamento</Label>
                <Input data-testid={PLEDGE.startDate} type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5"/>
              </div>
              {editing && (
                <div>
                  <Label>Stato</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attivo">Attivo</SelectItem>
                      <SelectItem value="in_attesa">In attesa</SelectItem>
                      <SelectItem value="sospeso">Sospeso</SelectItem>
                      <SelectItem value="chiuso">Chiuso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid={PLEDGE.saveBtn} className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
              {saveMut.isPending ? "Salvataggio…" : (editing ? "Salva modifiche" : "Salva")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="divide-y divide-stone-100">
        {pledges.length === 0 && <div className="p-6 text-sm text-stone-500">Nessun impegno registrato.</div>}
        {pledges.map((p) => (
          <div key={p.id} className="p-4 flex items-center justify-between text-sm gap-3">
            <div className="min-w-0">
              <div className="font-medium">
                {EUR.format(p.amount)} · {p.frequency === "una_tantum" ? "Una tantum" : `${p.kind} — ${p.frequency}`}
              </div>
              <div className="text-[11px] text-stone-500">Dal {fmtDate(p.start_date)} · {p.method}</div>
              {p.notes && <div className="text-[11px] text-stone-500 mt-1 truncate">{p.notes}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusBadge status={p.status}/>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`pledge-edit-${p.id}`}
                onClick={() => openEdit(p)}
                className="h-8 w-8 rounded-full hover:bg-stone-100"
                title="Modifica impegno"
              >
                <Pencil size={14}/>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`pledge-delete-${p.id}`}
                onClick={() => setDeletePledgeId(p.id)}
                className="h-8 w-8 rounded-full hover:bg-red-50 text-red-700"
                title="Elimina impegno"
              >
                <Trash2 size={14}/>
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deletePledgeId} onOpenChange={(o) => { if (!o) setDeletePledgeId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l&apos;impegno?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno rimosse anche le scadenze future non ancora pagate. I pagamenti già registrati restano.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => delMut.mutate(deletePledgeId)}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function SchedulesCard({ schedules, pledges, donorId, onChange }) {
  const qc = useQueryClient();
  const [silentPay, setSilentPay] = useState(null); // schedule in cui registro pagamento
  const [silentForm, setSilentForm] = useState({ payment_date: "", method: "bonifico", reference: "", is_historical: false });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ payment_date: new Date().toLocaleDateString("en-CA"), method: "bonifico", up_to_date: "", limit_to_date: false });

  const pendingSchedules = schedules.filter((s) => s.status !== "pagata");
  const pendingTotal = pendingSchedules.reduce((sum, s) => sum + (s.amount || 0), 0);

  const patch = useMutation({
    mutationFn: async ({ id, ...body }) => (await api.patch(`/schedules/${id}`, body)).data,
    onSuccess: () => { toast.success("Scadenza aggiornata"); onChange(); },
  });

  const silentPayMut = useMutation({
    mutationFn: async ({ schedule, form }) => (await api.post("/payments", {
      donor_id: donorId,
      pledge_id: schedule.pledge_id,
      schedule_id: schedule.id,
      amount: schedule.amount,
      payment_date: form.payment_date || schedule.due_date,
      method: form.method,
      reference: form.reference || null,
      silent: !!form.is_historical,
    })).data,
    onSuccess: (_data, vars) => {
      toast.success(vars.form.is_historical ? "Registrata come storico (non conta nei totali)" : "Donazione registrata");
      setSilentPay(null);
      qc.invalidateQueries({ queryKey: ["payments", donorId] });
      qc.invalidateQueries({ queryKey: ["schedules", donorId] });
      onChange();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const bulkPayMut = useMutation({
    mutationFn: async (form) => (await api.post("/schedules/bulk-silent-pay", {
      donor_id: donorId,
      payment_date: form.payment_date,
      method: form.method,
      up_to_date: form.limit_to_date ? form.up_to_date : null,
      notes: "Registrazione bulk silenziosa",
    })).data,
    onSuccess: (data) => {
      toast.success(`${data.count} scadenze registrate · totale ${new Intl.NumberFormat("it-IT",{style:"currency",currency:"EUR"}).format(data.total_amount)}`);
      setBulkOpen(false);
      qc.invalidateQueries({ queryKey: ["payments", donorId] });
      qc.invalidateQueries({ queryKey: ["schedules", donorId] });
      onChange();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const openSilent = (s) => {
    setSilentForm({ payment_date: new Date().toLocaleDateString("en-CA"), method: "bonifico", reference: "", is_historical: false });
    setSilentPay(s);
  };

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-4 border-b border-stone-100 flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Scadenze generate</div>
        {pendingSchedules.length > 0 && (
          <Button
            size="sm" variant="outline" className="rounded-full text-xs"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-silent-btn"
            title="Import storico: marca tutte le scadenze non ancora pagate come già registrate (non contano nei totali)"
          >
            Import storico ({pendingSchedules.length})
          </Button>
        )}
      </div>
      <div className="divide-y divide-stone-100 max-h-[420px] overflow-y-auto">
        {schedules.length === 0 && <div className="p-6 text-sm text-stone-500">Nessuna scadenza. Crea un impegno per generarle.</div>}
        {schedules.map((s) => (
          <div key={s.id} className="p-4 flex items-center justify-between text-sm" data-testid={`schedule-row-${s.id}`}>
            <div>
              <div className="font-medium">{EUR.format(s.amount)}</div>
              <div className="text-[11px] text-stone-500">Scade {fmtDate(s.due_date)}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={s.status}/>
              {s.status !== "pagata" && (
                <>
                  <Button size="sm" variant="outline" className="rounded-full text-xs"
                    onClick={() => openSilent(s)} data-testid={`schedule-silent-${s.id}`}>
                    Registra pagamento
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-xs text-stone-500"
                    onClick={() => patch.mutate({ id: s.id, status: "saltata" })}>
                    Salta
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!silentPay} onOpenChange={(v) => !v && setSilentPay(null)}>
        <DialogContent className="max-w-md" data-testid="silent-pay-dialog">
          <DialogHeader>
            <DialogTitle>Registra pagamento scadenza</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Importo</Label>
              <div className="font-semibold text-lg text-[#1C352D]">{silentPay && EUR.format(silentPay.amount)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data pagamento</Label>
                <Input type="date" value={silentForm.payment_date}
                  onChange={(e) => setSilentForm({ ...silentForm, payment_date: e.target.value })}
                  className="mt-1" data-testid="silent-date"/>
              </div>
              <div>
                <Label className="text-xs">Metodo</Label>
                <Select value={silentForm.method} onValueChange={(v) => setSilentForm({ ...silentForm, method: v })}>
                  <SelectTrigger className="mt-1" data-testid="silent-method"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Riferimento (opz.)</Label>
              <Input value={silentForm.reference} onChange={(e) => setSilentForm({ ...silentForm, reference: e.target.value })}
                placeholder="Es. CRO bonifico, n° ricevuta…" className="mt-1" data-testid="silent-ref"/>
            </div>
            <label className={`flex items-start gap-2 text-xs cursor-pointer rounded-md border p-3 transition-colors ${silentForm.is_historical ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-200 hover:border-stone-300"}`}>
              <input type="checkbox" checked={silentForm.is_historical}
                onChange={(e) => setSilentForm({ ...silentForm, is_historical: e.target.checked })}
                className="mt-0.5" data-testid="silent-historical"/>
              <div>
                <div className="font-medium text-stone-700">Solo registrazione storica (non conta nei totali)</div>
                <div className="text-stone-500 mt-0.5">
                  Attiva se questa donazione è già stata registrata altrove (es. import massivo dello storico).
                  Se stai <b>ricevendo davvero ora</b> il pagamento, lascia deselezionato.
                </div>
              </div>
            </label>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSilentPay(null)}>Annulla</Button>
            <Button onClick={() => silentPayMut.mutate({ schedule: silentPay, form: silentForm })}
              disabled={silentPayMut.isPending} data-testid="silent-confirm"
              className={silentForm.is_historical ? "bg-amber-600 hover:bg-amber-700" : "bg-[#1C352D] hover:bg-[#264A3E]"}>
              {silentPayMut.isPending ? "Registrazione…" : (silentForm.is_historical ? "Registra come storico" : "Registra pagamento")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-silent-dialog">
          <DialogHeader>
            <DialogTitle>Registra tutte le scadenze pending</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-stone-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
            📌 Verranno registrate come <b>già ricevute</b> tutte le scadenze non ancora pagate, in modalità <b>silenziosa</b> (nessuna notifica al donatore).
          </div>
          <div className="rounded-md bg-stone-50 border border-stone-200 p-3 mb-4 text-sm">
            <div className="flex justify-between mb-1"><span className="text-stone-500">Scadenze pending</span><b>{pendingSchedules.length}</b></div>
            <div className="flex justify-between"><span className="text-stone-500">Totale</span><b className="text-[#1C352D]">{EUR.format(pendingTotal)}</b></div>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data pagamento *</Label>
                <Input type="date" value={bulkForm.payment_date}
                  onChange={(e) => setBulkForm({ ...bulkForm, payment_date: e.target.value })}
                  className="mt-1" data-testid="bulk-date"/>
              </div>
              <div>
                <Label className="text-xs">Metodo</Label>
                <Select value={bulkForm.method} onValueChange={(v) => setBulkForm({ ...bulkForm, method: v })}>
                  <SelectTrigger className="mt-1" data-testid="bulk-method"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={bulkForm.limit_to_date}
                onChange={(e) => setBulkForm({ ...bulkForm, limit_to_date: e.target.checked })}
                data-testid="bulk-limit"/>
              Limita alle scadenze fino a una certa data
            </label>
            {bulkForm.limit_to_date && (
              <div>
                <Label className="text-xs">Solo scadenze fino a</Label>
                <Input type="date" value={bulkForm.up_to_date}
                  onChange={(e) => setBulkForm({ ...bulkForm, up_to_date: e.target.value })}
                  className="mt-1" data-testid="bulk-upto"/>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Annulla</Button>
            <Button onClick={() => bulkPayMut.mutate(bulkForm)}
              disabled={bulkPayMut.isPending || !bulkForm.payment_date || (bulkForm.limit_to_date && !bulkForm.up_to_date)}
              data-testid="bulk-confirm"
              className="bg-[#1C352D] hover:bg-[#264A3E]">
              {bulkPayMut.isPending ? "Registrazione…" : `Registra ${pendingSchedules.length} scadenze`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PaymentsCard({ payments, donorId, pledges, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    amount: "", payment_date: new Date().toISOString().slice(0, 10),
    method: "bonifico", reference: "", schedule_id: "", notes: "",
    receipt_path: null, receipt_filename: null,
  });
  const create = useMutation({
    mutationFn: async () => {
      const body = { ...form, donor_id: donorId, amount: parseFloat(form.amount) };
      if (!body.schedule_id) delete body.schedule_id;
      return (await api.post("/payments", body)).data;
    },
    onSuccess: () => { toast.success("Pagamento registrato"); setOpen(false); onChange();
      setForm({ ...form, amount: "", reference: "", schedule_id: "", receipt_path: null, receipt_filename: null }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/payments/${id}`)).data,
    onSuccess: () => { toast.success("Pagamento eliminato"); onChange(); },
  });

  async function uploadReceipt(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/payments/upload-receipt", fd);
    setForm({ ...form, receipt_path: data.storage_path, receipt_filename: data.filename });
    toast.success("Ricevuta caricata");
  }

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-4 flex items-center justify-between border-b border-stone-100">
        <div className="text-sm font-medium">Pagamenti ricevuti</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid={PAYMENT.addBtn} className="rounded-full bg-[#E2725B] hover:bg-[#c85d47] text-white transition-colors duration-150">
              <Plus size={14} className="mr-1"/> Registra pagamento
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Registra pagamento</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Importo (€)</Label>
                  <Input data-testid={PAYMENT.amount} type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5"/>
                </div>
                <div>
                  <Label>Data</Label>
                  <Input data-testid={PAYMENT.date} type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Metodo</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger data-testid={PAYMENT.method} className="mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bonifico">Bonifico</SelectItem>
                      <SelectItem value="contanti">Contanti</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Riferimento</Label>
                  <Input data-testid={PAYMENT.reference} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" placeholder="CRO / causale"/>
                </div>
              </div>
              <div>
                <Label>Collega a scadenza (opzionale)</Label>
                <Select value={form.schedule_id || "none"} onValueChange={(v) => setForm({ ...form, schedule_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Nessuna scadenza"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Donazione libera</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-stone-500 mt-1">Puoi collegare le scadenze dalla tab Scadenze.</div>
              </div>
              <div>
                <Label>Ricevuta (PDF/immagine)</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 border border-stone-200 rounded-md px-3 py-1.5 text-sm hover:bg-stone-50 transition-colors duration-150">
                    <Upload size={14}/> Carica file
                    <input data-testid={PAYMENT.uploadReceipt} type="file" className="hidden" onChange={uploadReceipt} accept=".pdf,.png,.jpg,.jpeg,.webp"/>
                  </label>
                  {form.receipt_filename && <span className="text-xs text-stone-600">{form.receipt_filename}</span>}
                </div>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5"/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid={PAYMENT.saveBtn} className="bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => create.mutate()}>Salva pagamento</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="divide-y divide-stone-100">
        {payments.length === 0 && <div className="p-6 text-sm text-stone-500">Nessun pagamento registrato.</div>}
        {payments.map((p) => (
          <div key={p.id} className="p-4 flex items-center justify-between text-sm" data-testid={`payment-row-${p.id}`}>
            <div>
              <div className="font-medium flex items-center gap-2">
                {EUR.format(p.amount)} · {p.method}
                {p.silent && (
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" title="Registrazione silenziosa: non conta nei totali del donatore">storico</span>
                )}
              </div>
              <div className="text-[11px] text-stone-500">{fmtDate(p.payment_date)}{p.reference ? ` · ${p.reference}` : ""}</div>
            </div>
            <button className="text-stone-400 hover:text-red-600 transition-colors duration-150"
              onClick={() => { if (window.confirm("Eliminare il pagamento?")) del.mutate(p.id); }}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DocumentsCard({ donorId }) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("Sottoscrizione firmata");
  const { data: docs = [] } = useQuery({
    queryKey: ["donor-documents", donorId],
    queryFn: async () => (await api.get(`/donors/${donorId}/documents`)).data,
  });

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || "Documento");
      await api.post(`/donors/${donorId}/documents`, fd);
      toast.success("Documento caricato");
      qc.invalidateQueries({ queryKey: ["donor-documents", donorId] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  }

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/donors/documents/${id}`)).data,
    onSuccess: () => { toast.success("Documento eliminato"); qc.invalidateQueries({ queryKey: ["donor-documents", donorId] }); },
  });

  function openDocument(doc) {
    const token = localStorage.getItem("token");
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/donors/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`;
    openFile(url, doc.original_filename || doc.filename);
  }

  function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function iconFor(ext) {
    if (ext === "pdf") return <FileText size={18} className="text-red-600"/>;
    if (["png", "jpg", "jpeg", "webp"].includes(ext)) return <FileIcon size={18} className="text-emerald-700"/>;
    return <FileIcon size={18} className="text-stone-500"/>;
  }

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-4 border-b border-stone-100 space-y-3">
        <div className="text-sm font-medium">Documenti del donatore</div>
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Etichetta documento</Label>
            <Input
              data-testid="doc-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1"
              placeholder="Es. Sottoscrizione firmata, Modulo privacy…"
            />
          </div>
          <label className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-[#1C352D] hover:bg-[#264A3E] text-white px-4 h-10 text-sm transition-colors duration-150 md:mt-6 md:self-end">
            <Upload size={14}/> {uploading ? "Caricamento…" : "Carica documento"}
            <input
              data-testid="doc-upload-input"
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={onUpload}
              disabled={uploading}
            />
          </label>
        </div>
        <div className="text-[11px] text-stone-500">
          Formati: PDF, PNG, JPG, JPEG, WEBP · Max 15 MB per file.
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {docs.length === 0 && (
          <div className="p-6 text-sm text-stone-500">
            Nessun documento caricato. Carica la sottoscrizione firmata, i moduli privacy o altri allegati.
          </div>
        )}
        {docs.map((d) => (
          <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`doc-row-${d.id}`}>
            <div className="w-10 h-10 rounded-lg bg-stone-50 border border-stone-200 grid place-items-center shrink-0">
              {iconFor(d.extension)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{d.label}</div>
              <div className="text-[11px] text-stone-500 truncate">
                {d.original_filename} · {formatSize(d.size)} · caricato {fmtDate(d.uploaded_at)}
              </div>
            </div>
            <button
              onClick={() => openDocument(d)}
              className="text-stone-500 hover:text-[#1C352D] p-2 rounded-full hover:bg-stone-50 transition-colors duration-150"
              title="Apri"
              data-testid={`doc-open-${d.id}`}
            >
              <FileDown size={15}/>
            </button>
            <button
              onClick={() => { if (window.confirm(`Eliminare "${d.label}"?`)) del.mutate(d.id); }}
              className="text-stone-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors duration-150"
              title="Elimina"
              data-testid={`doc-delete-${d.id}`}
            >
              <Trash2 size={15}/>
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NotesCard({ donor }) {
  return (
    <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
      <div className="text-sm font-medium mb-2">Note relazionali</div>
      <div className="text-sm text-stone-700 whitespace-pre-wrap min-h-[80px]">
        {donor.notes || <span className="text-stone-400">Nessuna nota inserita.</span>}
      </div>
    </Card>
  );
}

function TimelineList({ events }) {
  if (!events.length) return <div className="text-xs text-stone-500">Nessuna attività registrata.</div>;
  return (
    <ol className="relative border-l border-stone-200 ml-2 space-y-4">
      {events.slice(0, 30).map((e, i) => {
        const isPay = e.kind === "payment";
        return (
          <li key={e.id || i} className="ml-4 animate-in-row" style={{ animationDelay: `${i * 20}ms` }}>
            <span className={`absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full ${isPay ? "bg-[#E2725B]" : "bg-[#1C352D]"} ring-2 ring-white`} />
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{isPay ? "Pagamento" : `Comunicazione · ${e.channel}`}</div>
            <div className="text-sm mt-0.5">
              {isPay
                ? <span className="font-medium">{EUR.format(e.amount)}</span>
                : <span className="font-medium">{e.subject || "(senza oggetto)"}</span>}
            </div>
            <div className="text-[11px] text-stone-500">{fmtDate(e.date)}</div>
          </li>
        );
      })}
    </ol>
  );
}

function CommunicationBox({ donorId, donor, onSent }) {
  const [form, setForm] = useState({ channel: "email", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const submit = async () => {
    setSending(true);
    try {
      await api.post("/communications", {
        donor_id: donorId,
        channel: form.channel,
        subject: form.subject,
        body: form.body,
        status: form.channel === "email" ? "inviato" : "registrato",
      });
      toast.success("Comunicazione registrata");
      setForm({ ...form, subject: "", body: "" });
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSending(false); }
  };
  return (
    <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
      <div className="text-sm font-medium mb-3">Registra contatto</div>
      <div className="space-y-3">
        <div className="flex gap-2">
          {[
            { v: "email", label: "Email", icon: Mail },
            { v: "whatsapp", label: "WhatsApp", icon: MessageSquare },
            { v: "telefono", label: "Telefono", icon: MessageSquare },
            { v: "nota", label: "Nota", icon: StickyNote },
          ].map(({ v, label, icon: Ic }) => (
            <button key={v} onClick={() => setForm({ ...form, channel: v })}
              className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 border transition-colors duration-150 ${form.channel === v ? "bg-[#1C352D] text-white border-[#1C352D]" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}
              data-testid={`comm-channel-${v}`}>
              <Ic size={12}/> {label}
            </button>
          ))}
        </div>
        {form.channel === "email" && (
          <Input placeholder="Oggetto" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}/>
        )}
        <Textarea placeholder="Corpo del messaggio o nota interna…" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}/>
        <Button
          disabled={!form.body || sending}
          data-testid="comm-send"
          onClick={submit}
          className="w-full rounded-full bg-[#E2725B] hover:bg-[#c85d47] text-white transition-colors duration-150">
          {sending ? "Salvataggio…" : "Salva & registra"}
        </Button>
        <div className="text-[11px] text-stone-500">
          Email: se BREVO_API_KEY è configurata, l&apos;email verrà inviata realmente; altrimenti sarà solo registrata nel timeline.
        </div>
      </div>
    </Card>
  );
}
